/**
 * Right now: the plays of the last hour minute by minute (YouTube Studio's realtime card) and, for a
 * brand that does live, each event on air with how many watch it. Refreshed every minute with the
 * rest of the realtime read; "Watching now" itself is the first KPI tile (kpi-strip.tsx). The
 * last hour's line is drawn taller than a tile's sparkline (it is the card's chart), but not so tall
 * that a single play reads as a surge.
 */

import ListRow from "@web-ui/components/ListRow";
import Skeleton from "@web-ui/components/Skeleton";
import StatusDot from "@web-ui/components/StatusDot";
import type { Realtime } from "../api";
import { Sparkline } from "../charts/sparkline";
import { MetricFigure } from "../components/metric-figure";
import { definition } from "../definitions";
import { count, minuteLabel } from "../format";
import type { ItemRef } from "../item-names";
import type { Report } from "../use-report";
import type { Pack } from "@/vocabulary/vocabulary";

export function RightNow({ report, pack, nameOf, showLive }: { report: Report<Realtime>; pack: Pack; nameOf: (id: string) => ItemRef; /** The brand does live: its events on air are listed. */ showLive: boolean }) {
  const r = report.data;
  const lastHour = r?.lastHour.reduce((n, m) => n + m.plays, 0) ?? 0;
  const updated = r?.lastHour.at(-1)?.minute;
  return (
    <section className="card flex flex-col gap-3" aria-labelledby="right-now" aria-busy={(!r && !report.failed) || undefined}>
      <div className="flex flex-wrap items-baseline gap-x-3 ps-card-inset">
        <h2 id="right-now" className="text-sub-title-1">Right now</h2>
        {r && updated !== undefined ? <span className="text-caption text-t-tertiary">Updated {minuteLabel(updated)}</span> : null}
      </div>
      {!r ? (
        report.failed ? <p className="text-body-2 text-t-secondary ps-card-inset pb-2">Live numbers are not available right now.</p>
          : <div className="px-card-inset pb-2"><Skeleton lines={showLive ? 4 : 2} /></div>
      ) : (
        <>
          <div className="flex flex-col gap-2 px-card-inset">
            <MetricFigure definition={definition("playsLastHour", pack)} value={count(lastHour)} />
            {lastHour > 0 ? (
              <div className="flex flex-col gap-1">
                <div className="h-12 xl:h-20"><Sparkline values={r.lastHour.map((m) => m.plays)} fill /></div>
                <div className="flex justify-between text-caption text-t-tertiary" aria-hidden="true"><span>60 min ago</span><span>Now</span></div>
              </div>
            ) : <p className="text-body-2 text-t-secondary">No plays started in the last hour.</p>}
          </div>
          {showLive ? (
            <div className="flex flex-col">
              {r.live.length ? r.live.map((e) => {
                const item = nameOf(e.itemId);
                return <ListRow key={e.itemId} leading={<StatusDot tone="critical" label="Live now" />} title={item.label} href={item.href} detail={`Live now · ${pack.nouns.liveEvent.one}`} meta={<span className="text-body-2 tabular-nums text-t-primary">{count(e.watchingNow)} watching</span>} />;
              }) : <p className="text-body-2 text-t-secondary ps-card-inset py-2">{`No ${pack.nouns.liveEvent.many.toLowerCase()} are on air right now.`}</p>}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
